import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Fab,
	Paper,
	Typography,
	IconButton,
	TextField,
	InputAdornment,
	CircularProgress,
	Avatar,
	Zoom,
} from "@mui/material";
import {
	SmartToy as RobotIcon,
	Close as CloseIcon,
	Send as SendIcon,
	ExpandMore as MinimizeIcon,
	DeleteSweep as ClearIcon,
	Mic as MicIcon,
	VolumeUp as VolumeUpIcon,
	VolumeOff as VolumeOffIcon,
	Delete as DeleteIcon,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import reqAI from "../../services/ai/reqAI";

const estiloMarkdown = {
	fontSize: "0.875rem",
	overflowX: "auto",
	"& p": { m: 0, mb: 1 },
	"& p:last-child": { mb: 0 },
	"& ul, & ol": { pl: 2.5, my: 0.5 },
	"& table": { borderCollapse: "collapse", width: "100%", my: 1, fontSize: "0.8rem" },
	"& th, & td": { border: "1px solid #e0e0e0", px: 0.75, py: 0.5, textAlign: "left" },
	"& th": { bgcolor: "#f1f4f9" },
};

const MAX_CARACTERES = 1000;
const Reconhecimento = window.SpeechRecognition || window.webkitSpeechRecognition;
const suportaLeitura = "speechSynthesis" in window;

const textoParaFala = (markdown) =>
	markdown
		.replace(/[*_#`|>]/g, " ")
		.replace(/-{3,}/g, " ")
		.replace(/\s+/g, " ")
		.trim();

if (suportaLeitura) window.speechSynthesis.getVoices();

const PREFERENCIA_VOZES = [/natural/i, /google/i, /./];

const prioridadeDaVoz = (voz) => PREFERENCIA_VOZES.findIndex((padrao) => padrao.test(voz.name));

const escolherVoz = () =>
	window.speechSynthesis
		.getVoices()
		.filter((voz) => voz.lang.replace("_", "-").startsWith("pt-BR"))
		.sort((a, b) => prioridadeDaVoz(a) - prioridadeDaVoz(b))[0];

const falar = (texto) => {
	window.speechSynthesis.cancel();
	const fala = new SpeechSynthesisUtterance(textoParaFala(texto));
	fala.lang = "pt-BR";
	fala.voice = escolherVoz() ?? null;
	fala.rate = 1.05;
	window.speechSynthesis.speak(fala);
};

const pararFala = () => suportaLeitura && window.speechSynthesis.cancel();

const MICROFONE_BLOQUEADO = `O microfone está bloqueado para este site. Para liberar:

1. Clique no **cadeado** ao lado do endereço do site
2. Em **Microfone**, escolha **Permitir**
3. **Recarregue** a página e clique no microfone de novo`;

const errosDeVoz = {
	"not-allowed": MICROFONE_BLOQUEADO,
	"service-not-allowed": "Este navegador não permite o reconhecimento de voz. Use o Chrome ou o Edge.",
	"audio-capture": "Não encontrei nenhum microfone ligado ao computador.",
	network: "O reconhecimento de voz não conseguiu se conectar. Verifique a internet ou use o Chrome ou o Edge.",
};

const formatarTempo = (segundos) => `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, "0")}`;

const mensagemDeErro = (error) =>
	error instanceof TypeError
		? "Não consegui falar com o servidor de IA. Verifique sua conexão e tente de novo."
		: error.message;

const FloatingSiChat = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState([
		{
			id: "welcome",
			role: "assistant",
			content: "Olá! 👋 Sou o Sz Assistente, seu suporte inteligente. Como posso te ajudar hoje?",
		},
	]);
	const [input, setInput] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [conversaId, setConversaId] = useState(() => crypto.randomUUID());
	const [gravando, setGravando] = useState(false);
	const [transcricao, setTranscricao] = useState("");
	const [segundos, setSegundos] = useState(0);
	const [lerRespostas, setLerRespostas] = useState(false);
	const messagesEndRef = useRef(null);
	const inputRef = useRef(null);
	const reconhecimentoRef = useRef(null);
	const gravacaoRef = useRef({ final: "", atual: "", acao: null });
	const enviarRef = useRef(null);

	const scrollToBottom = () => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	};

	useEffect(() => {
		if (isOpen) {
			scrollToBottom();
		}
	}, [messages, isOpen]);

	useEffect(() => {
		if (!gravando) return;
		const timer = setInterval(() => setSegundos((atual) => atual + 1), 1000);
		return () => clearInterval(timer);
	}, [gravando]);

	const terminarGravacao = (acao) => {
		gravacaoRef.current.acao = acao;
		reconhecimentoRef.current?.stop();
	};

	useEffect(() => {
		if (isOpen) {
			inputRef.current?.focus();
			return;
		}
		terminarGravacao("descartar");
		pararFala();
	}, [isOpen]);

	const avisar = (texto) =>
		setMessages((prev) => [...prev, { id: Date.now().toString(), role: "assistant", content: texto }]);

	const iniciarGravacao = async () => {
		const permissao = await navigator.permissions?.query({ name: "microphone" }).catch(() => null);
		if (permissao?.state === "denied") {
			avisar(MICROFONE_BLOQUEADO);
			return;
		}

		pararFala();
		gravacaoRef.current = { final: "", atual: "", acao: null };
		setTranscricao("");
		setSegundos(0);

		const reconhecimento = new Reconhecimento();
		reconhecimento.lang = "pt-BR";
		reconhecimento.continuous = true;
		reconhecimento.interimResults = true;

		reconhecimento.onresult = (evento) => {
			let parcial = "";
			for (let i = evento.resultIndex; i < evento.results.length; i++) {
				const trecho = evento.results[i][0].transcript;
				if (evento.results[i].isFinal) gravacaoRef.current.final += `${trecho} `;
				else parcial += trecho;
			}
			gravacaoRef.current.atual = `${gravacaoRef.current.final}${parcial}`.trim().slice(0, MAX_CARACTERES);
			setTranscricao(gravacaoRef.current.atual);
		};

		reconhecimento.onerror = (evento) => {
			console.warn("[VOZ] erro no reconhecimento:", evento.error, evento.message);
			if (evento.error === "no-speech" || evento.error === "aborted") return;

			gravacaoRef.current.acao = "descartar";
			avisar(errosDeVoz[evento.error] ?? `Não consegui usar o microfone (${evento.error}).`);
		};

		reconhecimento.onend = () => {
			const { acao, atual } = gravacaoRef.current;
			console.log("[VOZ] reconhecimento encerrado | ação:", acao ?? "reiniciar", "| texto:", atual);
			if (!acao) {
				reconhecimento.start();
				return;
			}
			setGravando(false);
			if (acao === "enviar") enviarRef.current(atual);
			inputRef.current?.focus();
		};

		reconhecimentoRef.current = reconhecimento;
		reconhecimento.start();
		setGravando(true);
	};

	const alternarLeitura = () => {
		if (lerRespostas) pararFala();
		setLerRespostas(!lerRespostas);
	};

	const handleSend = (e) => {
		if (e) e.preventDefault();
		enviarMensagem(input);
		setInput("");
	};

	const enviarMensagem = async (texto) => {
		if (!texto.trim() || isSending) return;

		pararFala();

		const userMessage = { id: Date.now().toString(), role: "user", content: texto };
		const assistantId = (Date.now() + 1).toString();
		const placeholderMessage = { id: assistantId, role: "assistant", content: "", streaming: true };

		setMessages((prev) => [...prev, userMessage, placeholderMessage]);
		setIsSending(true);

		try {
			const stream = await reqAI.sendMessageStream(texto, conversaId);
			const reader = stream.getReader();
			const decoder = new TextDecoder();
			let fullContent = "";

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				const chunk = decoder.decode(value, { stream: true });
				fullContent += chunk;

				setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: fullContent } : m)));
			}

			setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)));
			if (lerRespostas) falar(fullContent);
		} catch (error) {
			console.error("Erro no chat:", error);
			setMessages((prev) =>
				prev.map((m) => (m.id === assistantId ? { ...m, content: mensagemDeErro(error), streaming: false } : m)),
			);
		} finally {
			setIsSending(false);
			inputRef.current?.focus();
		}
	};

	enviarRef.current = enviarMensagem;

	const clearChat = () => {
		terminarGravacao("descartar");
		pararFala();
		setConversaId(crypto.randomUUID());
		setMessages([
			{
				id: "welcome",
				role: "assistant",
				content: "Conversa reiniciada. Como posso te ajudar agora?",
			},
		]);
	};

	return (
		<Box sx={{ position: "fixed", bottom: 60, right: 20, zIndex: 9999 }}>
			{/* Janela de Chat */}
			<Zoom in={isOpen}>
				<Paper
					elevation={6}
					sx={{
						position: "absolute",
						bottom: 70,
						right: 0,
						width: { xs: "90vw", sm: 380 },
						height: { xs: "70vh", sm: 500 },
						display: "flex",
						flexDirection: "column",
						borderRadius: 3,
						overflow: "hidden",
					}}
				>
					{/* Header */}
					<Box
						sx={{
							p: 2,
							background: "linear-gradient(135deg, #3c5f97 0%, #2a4365 100%)",
							color: "white",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
							<Avatar sx={{ bgcolor: "white", color: "#3c5f97", width: 32, height: 32 }}>
								<RobotIcon fontSize="small" />
							</Avatar>
							<Box>
								<Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
									Sz Assistente
								</Typography>
								<Typography variant="caption" sx={{ opacity: 0.8 }}>
									Online
								</Typography>
							</Box>
						</Box>
						<Box>
							{suportaLeitura && (
								<IconButton
									size="small"
									onClick={alternarLeitura}
									sx={{ color: "white" }}
									title={lerRespostas ? "Parar de ler as respostas" : "Ler as respostas em voz alta"}
								>
									{lerRespostas ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
								</IconButton>
							)}
							<IconButton
								size="small"
								onClick={clearChat}
								sx={{ color: "white" }}
								title="Limpar conversa"
							>
								<ClearIcon fontSize="small" />
							</IconButton>
							<IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: "white" }}>
								<MinimizeIcon />
							</IconButton>
						</Box>
					</Box>

					{/* Mensagens */}
					<Box
						sx={{
							flex: 1,
							overflowY: "auto",
							p: 2,
							display: "flex",
							flexDirection: "column",
							gap: 2,
							bgcolor: "#f8f9fa",
						}}
					>
						{messages.map((msg) => (
							<Box
								key={msg.id}
								sx={{
									alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
									maxWidth: "85%",
									display: "flex",
									flexDirection: "column",
									alignItems: msg.role === "user" ? "flex-end" : "flex-start",
								}}
							>
								<Paper
									elevation={1}
									sx={{
										p: 1.5,
										borderRadius: msg.role === "user" ? "15px 15px 2px 15px" : "15px 15px 15px 2px",
										bgcolor: msg.role === "user" ? "#3c5f97" : "white",
										color: msg.role === "user" ? "white" : "inherit",
									}}
								>
									{msg.role === "assistant" ? (
										<Box sx={estiloMarkdown}>
											<ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
											{msg.streaming && <span style={{ opacity: 0.5 }}>...</span>}
										</Box>
									) : (
										<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontSize: "0.875rem" }}>
											{msg.content}
										</Typography>
									)}
								</Paper>
								<Typography variant="caption" sx={{ mt: 0.5, color: "gray", px: 0.5 }}>
									{msg.role === "assistant" ? "Sz Assistente" : "Você"}
								</Typography>
							</Box>
						))}
						<div ref={messagesEndRef} />
					</Box>

					{/* Input */}
					<Box sx={{ p: 1.5, borderTop: "1px solid #eee", bgcolor: "white" }}>
						{gravando ? (
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<IconButton onClick={() => terminarGravacao("descartar")} size="small" title="Descartar gravação">
									<DeleteIcon />
								</IconButton>
								<Box
									sx={{
										flex: 1,
										minWidth: 0,
										display: "flex",
										alignItems: "center",
										gap: 1,
										px: 1.5,
										py: 1,
										borderRadius: 5,
										bgcolor: "#f1f4f9",
									}}
								>
									<Box
										sx={{
											width: 10,
											height: 10,
											flexShrink: 0,
											borderRadius: "50%",
											bgcolor: "error.main",
											animation: "piscar 1s infinite",
											"@keyframes piscar": { "50%": { opacity: 0.2 } },
										}}
									/>
									<Typography variant="body2" sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
										{formatarTempo(segundos)}
									</Typography>
									<Typography variant="body2" noWrap sx={{ minWidth: 0, color: "text.secondary" }}>
										{transcricao ? transcricao.slice(-60) : "Ouvindo..."}
									</Typography>
								</Box>
								<IconButton
									onClick={() => terminarGravacao("enviar")}
									color="primary"
									disabled={isSending}
									size="small"
									title="Enviar"
								>
									<SendIcon />
								</IconButton>
							</Box>
						) : (
							<form onSubmit={handleSend}>
								<TextField
									fullWidth
									size="small"
									placeholder="Digite sua mensagem..."
									value={input}
									onChange={(e) => setInput(e.target.value)}
									inputRef={inputRef}
									inputProps={{ maxLength: MAX_CARACTERES }}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												{Reconhecimento && !input.trim() ? (
													<IconButton
														onClick={iniciarGravacao}
														disabled={isSending}
														color="primary"
														size="small"
														title="Gravar mensagem de voz"
													>
														<MicIcon fontSize="small" />
													</IconButton>
												) : (
													<IconButton
														type="submit"
														color="primary"
														disabled={!input.trim() || isSending}
														size="small"
													>
														{isSending ? <CircularProgress size={20} /> : <SendIcon fontSize="small" />}
													</IconButton>
												)}
											</InputAdornment>
										),
										sx: { borderRadius: 5 },
									}}
								/>
							</form>
						)}
					</Box>
				</Paper>
			</Zoom>

			{/* Botão Flutuante */}
			<Fab
				color="primary"
				aria-label="chat"
				onClick={() => setIsOpen(!isOpen)}
				sx={{
					width: 56,
					height: 56,
					background: "linear-gradient(135deg, #3c5f97 0%, #2a4365 100%)",
					boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
					"&:hover": {
						background: "linear-gradient(135deg, #2a4365 0%, #3c5f97 100%)",
					},
				}}
			>
				{isOpen ? <CloseIcon /> : <RobotIcon />}
			</Fab>
		</Box>
	);
};

export default FloatingSiChat;
